import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  BookOpen, Clock, CheckCircle, Upload, AlertCircle, 
  Package, CreditCard, Banknote, Search, Info, XCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/libritos';

// Datos bancarios
const BANK_INFO = {
  cuenta: "Cuenta corriente",
  numero: "020782/1",
  cbu: "0140023601506802078210",
  alias: "cats.lomasdezamora",
  cuit: "33-64492045-9 / 30-68816043-6",
  entidad: "Colegio de Asistentes y Trabajadores Sociales"
};

const STATUS_CONFIG = {
  pendiente: { 
    label: "Pendiente de pago", 
    color: "bg-amber-100 text-amber-700",
    icon: Clock
  },
  comprobante_enviado: { 
    label: "Comprobante enviado", 
    color: "bg-blue-100 text-blue-700",
    icon: Upload
  },
  pagado: { 
    label: "Pagado - Listo para retirar", 
    color: "bg-green-100 text-green-700",
    icon: CheckCircle
  },
  entregado: { 
    label: "Entregado", 
    color: "bg-gray-100 text-gray-700",
    icon: Package
  },
  cancelada: { 
    label: "Cancelada", 
    color: "bg-red-100 text-red-700",
    icon: XCircle
  },
  vencida: { 
    label: "Vencida", 
    color: "bg-red-100 text-red-700",
    icon: AlertCircle
  }
};

export default function MisReservasPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [paymentInfoModal, setPaymentInfoModal] = useState(false);

  useEffect(() => {
    if (searchParams.get('email')) {
      fetchReservations(searchParams.get('email'));
    }
  }, [searchParams]);

  const fetchReservations = async (emailToSearch) => {
    if (!emailToSearch) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/mis-reservas?email=${emailToSearch}`);
      setReservations(response.data);
      setSearched(true);
    } catch (error) {
      toast.error("Error al buscar reservas");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReservations(email);
  };

  const openUploadModal = (reservation) => {
    setSelectedReservation(reservation);
    setUploadModal(true);
  };

  const handleUploadComprobante = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande (máx 5MB)");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API_URL}/reservations/${selectedReservation.id}/comprobante`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("¡Comprobante subido! Vamos a verificar tu pago.");
      setUploadModal(false);
      fetchReservations(email);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir comprobante");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pendiente;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-700 via-violet-600 to-purple-600 text-white py-8 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-3">
            <BookOpen className="h-8 w-8" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              Mis Reservas
            </h1>
          </div>
          <p className="text-violet-100 text-sm md:text-base">
            Consultá el estado de tus reservas y subí tu comprobante de pago
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Buscador */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="email" className="text-gray-700">Email con el que reservaste</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                className="mt-1"
                data-testid="mis-reservas-email-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700 self-end"
              data-testid="mis-reservas-search-btn"
            >
              <Search className="h-4 w-4 mr-2" />
              {loading ? "Buscando..." : "Buscar"}
            </Button>
          </form>
        </div>

        {/* Resultados */}
        {searched && (
          <>
            {reservations.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-violet-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No encontramos reservas
                </h3>
                <p className="text-gray-500">
                  No hay reservas asociadas a este email
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {reservations.map((res) => (
                  <div 
                    key={res.id} 
                    className="bg-white rounded-xl shadow-md overflow-hidden"
                    data-testid={`reservation-card-${res.id}`}
                  >
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">ID: {res.id}</p>
                          <h3 className="text-lg font-semibold text-gray-900">{res.book_title}</h3>
                        </div>
                        {getStatusBadge(res.status)}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-gray-500">Precio</p>
                          <p className="font-semibold text-violet-700">${res.book_price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Método de pago</p>
                          <p className="font-medium flex items-center gap-1">
                            {res.payment_method === 'transferencia' ? (
                              <><CreditCard className="h-4 w-4" /> Transferencia</>
                            ) : (
                              <><Banknote className="h-4 w-4" /> Efectivo</>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Reservado</p>
                          <p className="font-medium">{formatDate(res.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Vence</p>
                          <p className="font-medium text-amber-600">{formatDate(res.expires_at)}</p>
                        </div>
                      </div>

                      {/* Acciones según estado */}
                      {res.status === 'pendiente' && (
                        <div className="bg-violet-50 rounded-lg p-4 mt-4">
                          <p className="text-sm text-violet-800 mb-3">
                            <strong>¿Pagaste por transferencia?</strong> Subí tu comprobante para que podamos verificar el pago antes de que vengas.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button 
                              onClick={() => openUploadModal(res)}
                              className="bg-violet-600 hover:bg-violet-700"
                              data-testid={`upload-btn-${res.id}`}
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              Subir comprobante
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => setPaymentInfoModal(true)}
                              className="border-violet-300 text-violet-700"
                            >
                              <Info className="h-4 w-4 mr-2" />
                              Ver datos bancarios
                            </Button>
                          </div>
                        </div>
                      )}

                      {res.status === 'comprobante_enviado' && (
                        <div className="bg-blue-50 rounded-lg p-4 mt-4">
                          <p className="text-sm text-blue-800">
                            <strong>✓ Comprobante recibido.</strong> Estamos verificando tu pago. Te avisaremos cuando esté confirmado.
                          </p>
                          {res.comprobante_uploaded_at && (
                            <p className="text-xs text-blue-600 mt-2">
                              Subido el {formatDate(res.comprobante_uploaded_at)}
                            </p>
                          )}
                        </div>
                      )}

                      {res.status === 'pagado' && (
                        <div className="bg-green-50 rounded-lg p-4 mt-4">
                          <p className="text-sm text-green-800">
                            <strong>✓ Pago confirmado.</strong> Podés pasar a retirar tu libro por la sede del Colegio.
                          </p>
                        </div>
                      )}

                      {res.status === 'entregado' && (
                        <div className="bg-gray-50 rounded-lg p-4 mt-4">
                          <p className="text-sm text-gray-600">
                            <strong>✓ Libro entregado.</strong> ¡Gracias por tu compra!
                          </p>
                        </div>
                      )}

                      {(res.status === 'vencida' || res.status === 'cancelada') && (
                        <div className="bg-red-50 rounded-lg p-4 mt-4">
                          <p className="text-sm text-red-800">
                            Esta reserva ya no está activa. Si querés el libro, podés hacer una nueva reserva.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Info inicial */}
        {!searched && (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-violet-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Ingresá tu email para ver tus reservas
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Usá el mismo email con el que hiciste la reserva. Desde acá podés ver el estado y subir el comprobante de pago.
            </p>
          </div>
        )}
      </main>

      {/* Modal Subir Comprobante */}
      <Dialog open={uploadModal} onOpenChange={setUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-violet-900 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Subir Comprobante
            </DialogTitle>
            <DialogDescription>
              Subí una foto o captura de tu comprobante de transferencia
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-violet-50 rounded-lg p-4 text-sm">
              <p className="font-medium text-violet-800 mb-2">Reserva: {selectedReservation?.id}</p>
              <p className="text-violet-700">{selectedReservation?.book_title}</p>
              <p className="text-violet-600 font-bold">${selectedReservation?.book_price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</p>
            </div>
            
            <div>
              <Label htmlFor="comprobante">Seleccionar archivo</Label>
              <Input
                id="comprobante"
                type="file"
                accept="image/*,.pdf"
                onChange={handleUploadComprobante}
                disabled={uploading}
                className="mt-1"
                data-testid="comprobante-file-input"
              />
              <p className="text-xs text-gray-500 mt-1">Formatos: JPG, PNG, PDF. Máx 5MB</p>
            </div>

            {uploading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Subiendo...</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Datos Bancarios */}
      <Dialog open={paymentInfoModal} onOpenChange={setPaymentInfoModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-violet-900 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Datos para Transferencia
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-violet-50 rounded-lg p-4 space-y-2 text-sm">
              <p><strong>Cuenta:</strong> {BANK_INFO.cuenta}</p>
              <p><strong>N.º de cuenta:</strong> {BANK_INFO.numero}</p>
              <p><strong>CBU:</strong> <span className="font-mono">{BANK_INFO.cbu}</span></p>
              <p><strong>Alias:</strong> <span className="font-mono">{BANK_INFO.alias}</span></p>
              <p><strong>CUIT:</strong> {BANK_INFO.cuit}</p>
              <p><strong>Entidad:</strong> {BANK_INFO.entidad}</p>
            </div>
            <p className="text-sm text-gray-600">
              Una vez que hagas la transferencia, subí el comprobante desde esta página para que podamos verificar tu pago.
            </p>
            <Button
              onClick={() => setPaymentInfoModal(false)}
              className="w-full bg-violet-600 hover:bg-violet-700"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
